import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { BehaviorSubject, Observable, switchMap, take, tap, combineLatest, of } from 'rxjs';

import { AuthRepositoryService } from './auth-repository.service';
import { OktaTokenService } from './okta-token.service';
import { WINDOW } from '@cpak/shared/injectors';
import { getOktaUrl } from '../../consts/auth';
import { CryptoRandomString } from '../../enums/crypto-random-string';
import { PacksNotSavedChangesFacade, PresentationModeFacade, UserFacade } from '@cpak/main-features-lib/data-store';
import { LocalStorageService, StartService } from '@cpak/main-features-lib/services';
import { RoutingUrls } from '@cpak/enums';
import { TranslationService } from '../../localization/translation.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    @Inject(WINDOW) private window: Window,
    private authRepositoryService: AuthRepositoryService,
    private localStorage: LocalStorageService,
    private oktaTokenService: OktaTokenService,
    private router: Router,
    private route: ActivatedRoute,
    private startService: StartService,
    private presentationModeFacade: PresentationModeFacade,
    private userFacade: UserFacade,
    private packsNotSavedChangesFacade: PacksNotSavedChangesFacade,
    private translationService: TranslationService
  ) {}

  private authStatus: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private loginFailed: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private verificationTokenName = 'VERIFICATION_TOKEN';
  private lastActiveRoute = 'LAST_ROUTE';
  private callbackRoute = '/login/callback';

  public isCallBack(): boolean {
    return this.window.location.pathname === this.callbackRoute;
  }

  public setLoginFailedState(state: boolean): void {
    this.loginFailed.next(state);
  }

  public getLoginFailedState(): Observable<boolean> {
    return this.loginFailed.asObservable();
  }

  public setAuthState(state: boolean): void {
    this.authStatus.next(state);
  }

  public getAuthState(): Observable<boolean> {
    return this.authStatus.asObservable();
  }

  public refreshAuthToken(): Observable<void | null> {
    return this.authRepositoryService.refreshAuthToken();
  }

  public login(): void {
    const error = this.route.snapshot.queryParams['error'];

    if (error === 'access_denied') {
      const error_description = this.route.snapshot.queryParams['error_description'];
      this.setLoginFailedState(true);
      this.router.navigate([RoutingUrls.Forbidden], { queryParams: { error_description } });

      return;
    }

    const state = this.route.snapshot.queryParams['state'];
    const code = this.route.snapshot.queryParams['code'];

    this.authRepositoryService
      .login(state, code, this.getSavedVerificationToken())
      .pipe(
        tap(data => {
          const savedRoute = this.getSavedLastRoute();
          this.removeSavedRoute();
          this.removeVerificationToken();

          const userId = data?.data.user.id;
          if (userId) {
            this.userFacade.initUser(userId);
          }
          this.packsNotSavedChangesFacade.clearAllChanges();

          this.router.navigate([savedRoute]);
        }),
        switchMap(() => combineLatest([this.startService.getStart(), this.translationService.initTranslation()])),
        tap(([startResponse]) => {
          this.startService.setStartResponse(startResponse);
          this.setAuthState(true);
        }),
        switchMap(([startResponse]) =>
          combineLatest([of(startResponse), this.presentationModeFacade.getPresentationModeState()])
        ),
        take(1)
      )
      .subscribe(([startResponse, presentationMode]) => {
        if (presentationMode === null) {
          const userRole = startResponse?.data.userInfo.user.roles[0];
          const attributes = startResponse?.data.config.roles.find(role => role.key === userRole)?.attributes;
          const defaultPresentationMode = attributes ? !!attributes['presentationModeEnabledByDefault'] : false;
          this.presentationModeFacade.initPresentationMode(defaultPresentationMode);
        }
      });
  }

  public redirectOkta(): void {
    const randomToken = this.oktaTokenService.generateRandomString(49, CryptoRandomString.alphanumeric);
    const codeVerifier = this.oktaTokenService.generateBase64URL(randomToken);
    const codeChallenge = this.oktaTokenService.generateCodeChallenge(codeVerifier);
    const state = this.oktaTokenService.generateRandomString(49, CryptoRandomString.urlSafe);
    const nonce = this.oktaTokenService.generateRandomString(49, CryptoRandomString.urlSafe);
    this.setVerificationToken(codeVerifier);
    this.setLastRoute(this.router.routerState.snapshot.url);

    this.window.location.href = getOktaUrl(codeChallenge, state, nonce);
  }

  private getSavedVerificationToken(): string {
    return this.localStorage.getItemWithoutKey(this.verificationTokenName);
  }

  private setVerificationToken(token: string): void {
    this.localStorage.setItemWithoutKey(this.verificationTokenName, token);
  }

  private setLastRoute(route: string): void {
    this.localStorage.setItemWithoutKey(this.lastActiveRoute, route);
  }

  private getSavedLastRoute(): string {
    return this.localStorage.getItemWithoutKey(this.lastActiveRoute);
  }

  private removeVerificationToken(): void {
    this.localStorage.removeWithoutKey(this.verificationTokenName);
  }

  private removeSavedRoute(): void {
    this.localStorage.removeWithoutKey(this.lastActiveRoute);
  }
}
